import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Header from "@/components/Header";
import { supabase } from "@/lib/supabase";
import MysteryChat from "@/components/MysteryChat";
import { extractTitleWithConfidence } from "@/utils/titleExtraction";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Message } from "@/components/types";
import { Loader2, Zap, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

const MysteryChatPage = () => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<Message[]>([]);
    const [conversationData, setConversationData] = useState<any>(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isInitial = searchParams.get('initial') === 'true';
    const { isAuthenticated, user } = useAuth();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (id && isAuthenticated) {
            loadConversationData();
        }
    }, [id, isAuthenticated]);

    const loadConversationData = async () => {
        try {
            setIsLoadingHistory(true);
            
            const { data, error } = await supabase
                .from("conversations")
                .select("*, messages!fk_messages_conversation_id(*)")
                .eq("id", id)
                .single();

            if (error) {
                console.error("Error loading conversation:", error);
                toast.error(t("mysteryCreation.errors.loadConversationFailed"));
                return;
            }

            if (data) {
                setConversationData(data);
                
                // Load messages and sort by timestamp
                const formattedMessages = (data.messages || []).map((msg: any) => ({
                    id: msg.id,
                    content: msg.content,
                    is_ai: msg.role === "assistant",
                    timestamp: new Date(msg.created_at),
                    isAutoGenerated: false
                }));
                
                formattedMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
                setMessages(formattedMessages);
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error(t("mysteryCreation.errors.loadConversationFailed"));
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleSaveMessage = async (message: Message) => {
        if (!id) return;
        
        try {
            // supabase-js does NOT throw on insert failure — it returns { error }.
            // Without this check a failed save is invisible and the message
            // silently disappears on refresh.
            const { error: insertError } = await supabase.from("messages").insert({
                conversation_id: id,
                content: message.content,
                role: message.is_ai ? "assistant" : "user",
                is_ai: message.is_ai
            });
            if (insertError) throw insertError;

            // If this is an AI message, try to extract a title and update the conversation.
            // Only update when there's no real title yet — once a proper title is set,
            // later AI messages (help text, recaps, etc.) shouldn't overwrite it.
            //
            // Exception: a "# TITLE" heading match (isHeaderMatch) always wins. Titles are
            // extracted per-message as messages stream in, and an earlier message can contain
            // a bold 2-3 word phrase (a roster line, a clarifying question) that passes the
            // low-confidence bold-text fallback before the real "# TITLE" heading shows up in
            // a later message. Without this, that early guess ("The Sheriff", "The Empire")
            // permanently locks out the real title once it arrives, because by then the field
            // is no longer a placeholder. A header match is only ever produced by the model's
            // actual title line, so it's safe to let it overwrite a lower-confidence guess.
            if (message.is_ai) {
                const extracted = extractTitleWithConfidence([message]);
                if (extracted) {
                    const { data: existing } = await supabase
                        .from("conversations")
                        .select("title")
                        .eq("id", id)
                        .single();
                    const currentTitle = (existing?.title || "").trim();
                    // The initial title is always "<theme> - N Players" (set in MysteryCreation),
                    // so treat that raw format as a placeholder too — otherwise the AI's real
                    // title never overwrites it here. Extracted titles never end in " Players",
                    // so a real title, once set, won't get clobbered by later AI messages.
                    const isRawThemeTitle = / - \d+ players?$/i.test(currentTitle);
                    const isPlaceholder = !currentTitle ||
                        currentTitle.toLowerCase().startsWith("untitled") ||
                        currentTitle.toLowerCase().startsWith("new mystery") ||
                        isRawThemeTitle;
                    if (isPlaceholder || extracted.isHeaderMatch) {
                        await supabase
                            .from("conversations")
                            .update({ title: extracted.title })
                            .eq("id", id);
                    }
                }
            }

            // Update local messages state to keep button state in sync
            setMessages(prev => {
                const exists = prev.some(msg => msg.id === message.id);
                if (exists) {
                    return prev;
                }
                return [...prev, message].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
            });
        } catch (error) {
            console.error("Error saving message:", error);
            toast.error(t("mysteryCreation.errors.saveMessageFailed"));
        }
    };

    const handleGenerateFullMystery = async (messages: Message[]): Promise<void> => {
        navigate(`/mystery/purchase/${id}`);
    };

    // Check if AI has provided at least one response
    const hasAIResponse = messages.some(msg => msg.is_ai);

    if (isLoadingHistory) {
        return (
            <div className="min-h-screen flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center">
                    <div className="text-center px-4">
                        <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin mx-auto mb-4" />
                        <p className="text-sm sm:text-base text-muted-foreground">{t("common.messages.loadingContent")}</p>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-muted">
            <Header />
            <main className={cn(
                "flex-1", // Padding handled by chat component
                isMobile ? "pt-2 px-3 pb-40" : "pt-4 px-4 pb-32"
            )}>
                <div className={cn(
                    "mx-auto",
                    isMobile ? "max-w-full" : "max-w-4xl"
                )}>
                    {/* A paid order is created from the chat as it stood at payment, so later messages
                        change nothing (Santa, Interrupted and El Internado Del Cuervo kept designing for
                        hours after paying). Show the history only and say so. ADR-0103 Addendum 184. */}
                    {conversationData?.is_paid && (
                        <div className="mb-4 rounded-xl border bg-background p-4 sm:p-5 shadow-sm" role="status">
                            <div className="flex items-start gap-3">
                                <Lock className="h-5 w-5 mt-0.5 shrink-0 text-muted-foreground" />
                                <div className="space-y-3">
                                    <h2 className="font-semibold">{t("chat.paidNotice.title")}</h2>
                                    <p className="text-sm text-muted-foreground">{t("chat.paidNotice.body")}</p>
                                    <div className="flex flex-wrap gap-2">
                                        <Button onClick={() => navigate(`/mystery/${id}`)}>{t("chat.paidNotice.viewMystery")}</Button>
                                        <Button variant="outline" onClick={() => navigate("/support")}>{t("chat.paidNotice.contactSupport")}</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Chat Interface - Full Page Scrolling */}
                    <MysteryChat
                        initialTheme={conversationData?.mystery_data?.theme || conversationData?.theme || ""}
                        initialPlayerCount={conversationData?.mystery_data?.playerCount || conversationData?.player_count}
                        initialScriptType={(conversationData?.mystery_data?.scriptType || conversationData?.script_type) as 'full' | 'pointForm'}
                        initialAdditionalDetails={conversationData?.mystery_data?.additionalDetails || conversationData?.additional_details}
                        initialMysteryType={(conversationData?.mystery_data?.mysteryType || conversationData?.mystery_type || 'murder') as 'murder' | 'intrigue'}
                        initialMysteryStyle={conversationData?.mystery_data?.mysteryStyle || conversationData?.mystery_style}
                        savedMysteryId={id}
                        onSave={handleSaveMessage}
                        onGenerateFinal={handleGenerateFullMystery}
                        initialMessages={messages}
                        isLoadingHistory={false}
                        systemInstruction={conversationData?.system_instruction}
                        skipForm={true}
                        needsInitialAIResponse={isInitial}
                        usePageScroll={true} // New prop to enable page-level scrolling
                        readOnly={!!conversationData?.is_paid}
                    />
                </div>
            </main>
        </div>
    );
};

export default MysteryChatPage;
